import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { TargetBox } from 'components/DragDrop'
import { useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { toast } from 'react-toastify'
import { extractBorrowerFromDocument, uploadFiles } from 'services'
import { Button, Modal } from 'stories/components'

export type BorrowerAiField = {
  id?: string
  key: string
  title: string
  value: string | boolean | number
  display: string
  custom?: boolean
  section?: string
  target?: 'borrower' | 'loan' | 'pricing' | 'realEstate' | 'liability'
  borrowerSeperator?: string
  group?: string
}

type Props = {
  isOpen: boolean
  loanNumber: number
  borrowerSeperator: string
  /** Fields the open Borrower Details form is showing. */
  allowedKeys: string[]
  onClose: () => void
  onApply: (fields: BorrowerAiField[]) => void
}

export const BorrowerAiExtractModal = ({
  isOpen,
  loanNumber,
  borrowerSeperator,
  allowedKeys,
  onClose,
  onApply,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [fields, setFields] = useState<BorrowerAiField[]>([])
  const [included, setIncluded] = useState<Record<string, boolean>>({})
  const [fillable, setFillable] = useState(false)

  const reset = () => {
    setLoading(false)
    setFileName('')
    setFields([])
    setIncluded({})
    setFillable(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const applyResult = (rows: BorrowerAiField[], source?: string) => {
    const fromForm = source === 'fillable'
    setFillable(fromForm)
    const visible = new Set(allowedKeys)
    const shown = (rows || []).filter((row) => {
      if (row.value === '' || row.value == null) return false
      if (fromForm) return true
      return visible.has(row.key)
    })
    setFields(shown)
    setIncluded(Object.fromEntries(shown.map((row) => [row.id || row.key, true])))
    if (!shown.length) {
      toast(
        fromForm
          ? 'No filled fields were found in this application.'
          : 'No borrower details from this document match the fields on this screen.',
        { type: 'warning' },
      )
    }
  }

  const runExtractFromFileKey = async (fileKey: string, name: string) => {
    setLoading(true)
    setFields([])
    setFileName(name)
    try {
      const res = await extractBorrowerFromDocument([{ fileKey: String(fileKey), name }], borrowerSeperator)
      if (res?.success && res.data?.fields) applyResult(res.data.fields, res.data.source)
      else toast(res?.message || 'Failed to extract borrower details from the document.', { type: 'error' })
    } catch {
      toast('Failed to extract borrower details from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const runExtract = async (file: File) => {
    setLoading(true)
    setFields([])
    setFileName(file.name)
    try {
      const keys: string[] = await uploadFiles({ path: `loan/${loanNumber}/borrower-ai` }, [file])
      const fileKey = keys?.[0]
      if (!fileKey) {
        toast('Failed to upload the document.', { type: 'error' })
        return
      }
      const res = await extractBorrowerFromDocument([{ fileKey: String(fileKey), name: file.name }], borrowerSeperator)
      if (res?.success && res.data?.fields) applyResult(res.data.fields, res.data.source)
      else toast(res?.message || 'Failed to extract borrower details from the document.', { type: 'error' })
    } catch {
      toast('Failed to extract borrower details from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onFilePicked = (files: FileList | File[] | null) => {
    const file = files?.[0]
    if (!file) return
    runExtract(file)
  }

  const onDropItem = (item: any) => {
    if (item?.files?.length) {
      onFilePicked(item.files)
      return
    }
    const docs: any[] = Array.isArray(item?.documents) ? item.documents : [item]
    const doc = docs.find((d) => d?.fileKey || d?.key) || docs[0]
    const fileKey = doc?.fileKey || doc?.key
    const name = doc?.name || 'document'
    if (!fileKey) {
      toast('Could not read the dropped document.', { type: 'error' })
      return
    }
    runExtractFromFileKey(String(fileKey), name)
  }

  const rowId = (field: BorrowerAiField) => field.id || field.key
  const selected = fields.filter((field) => included[rowId(field)])

  return (
    <Modal
      title="Extract from Application"
      isOpen={isOpen}
      onClose={handleClose}
      titleOkay={selected.length ? 'Apply' : ''}
      onOk={() => {
        if (!selected.length) return
        onApply(selected)
        handleClose()
      }}
      loading={loading}
      disabled={!selected.length}
    >
      <div className="w-[720px] max-w-full space-y-4">
        <p className="text-sm text-gray-600">
          Drop a loan application here. A filled Business Purpose form is read from its fields and can update the
          loan, the property, real estate, and each borrower. Other files are read with AI for this borrower only.
          Nothing is saved until you apply.
        </p>

        <TargetBox
          types={['document', NativeTypes.FILE]}
          onDrop={onDropItem}
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-gray-300 hover:border-shade-blue"
          highlightClassName="border-shade-blue bg-blue-50"
        >
          <div onClick={() => inputRef.current?.click()}>
            <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-shade-blue mb-2" />
            <div className="font-medium text-gray-800">Drop a loan application here</div>
            <div className="text-sm text-gray-500 mt-1">PDF, image, Word, spreadsheet, or click to browse</div>
            {fileName && <div className="text-xs text-gray-500 mt-2">Selected: {fileName}</div>}
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </TargetBox>

        {loading && <div className="text-sm text-shade-blue font-medium">Reading the application…</div>}

        {!!fields.length && (
          <div className="space-y-3">
            <div className="font-semibold">Extracted fields</div>
            <div className="overflow-auto border rounded max-h-72">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-2 w-10" />
                    {fillable && <th className="p-2">Section</th>}
                    <th className="p-2">Field</th>
                    <th className="p-2">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => (
                    <tr key={rowId(field)} className="border-t">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={!!included[rowId(field)]}
                          onChange={(e) => setIncluded((prev) => ({ ...prev, [rowId(field)]: e.target.checked }))}
                        />
                      </td>
                      {fillable && <td className="p-2 text-gray-500">{field.section}</td>}
                      <td className="p-2 text-gray-600">{field.title}</td>
                      <td className="p-2">{field.display}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button link color="gray" onClick={reset}>
                Choose another file
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
